// FUNCTIONALITY-115C-R2 / NOVAPAY-116-RC3 — Store / legal content source of truth (UA facts + section bodies).
// Consumers: consent, About/Profile, checkout FOP, payment panel. Do not hardcode seller facts elsewhere.
// Long offer/privacy bodies load via legal-body-loader-v2 (route-level dynamic import).

import {
  STORE_INFO,
  formatSellerContactsText,
  formatSellerPaymentRequisitesText,
} from "./store-merchant-sot-v2.js";

export { STORE_INFO, formatSellerContactsText, formatSellerPaymentRequisitesText };

/** Bumped when customer-facing consent / legal text changes (re-accept on commercial cutover). */
export const CONSENT_VERSION = "CUSTOMER_LEGAL_R4_8_20260817";
/** Previous commercial package — R4.8 gate must appear once for R4.2 acceptors. */
export const CONSENT_VERSION_PREVIOUS = "CUSTOMER_LEGAL_R4_2_20260816";
/** Historical consent package (immutable receipts may still reference this). */
export const CONSENT_VERSION_LEGACY = "v6-2026-07-23-p0";

/** Canonical legal accordion order (no separate contacts accordion). */
export const LEGAL_SECTION_KINDS = [
  "about",
  "offer",
  "privacy",
  "payment",
  "bonuses",
  "returns",
  "food",
] as const;

export type LegalSectionKind = (typeof LEGAL_SECTION_KINDS)[number];

export interface InfoSection {
  kind?: LegalSectionKind | "contacts";
  title: string;
  body: string[];
  /** Structured payment method bullets (payment section). */
  paymentMethods?: string[];
  /** Optional external legal links (opened via external-link adapter). */
  links?: Array<{ label: string; href: string }>;
}

export const PAYMENT_METHODS_UA: string[] = [
  "Оплата на рахунок (IBAN).",
];

export const PAYMENT_METHODS_EN: string[] = [
  "Bank transfer to the sole proprietor (FOP) account.",
];

export const ABOUT_STORE: InfoSection = {
  kind: "about",
  title: "Про магазин і продавця",
  body: [
    `${STORE_INFO.name} — магазин оригінальних азійських смаків.`,
    STORE_INFO.slogan,
    STORE_INFO.scheduleLine,
  ],
};

export const USER_AGREEMENT: InfoSection = {
  kind: "offer",
  title: "Публічна оферта",
  body: [],
};

export const PRIVACY_POLICY: InfoSection = {
  kind: "privacy",
  title: "Політика конфіденційності",
  body: [],
};

export const PAYMENT_DELIVERY: InfoSection = {
  kind: "payment",
  title: "Оплата і доставка",
  body: [
    "Доставка здійснюється Новою Поштою по Україні згідно з чинними умовами та тарифами перевізника.",
    "Вартість доставки та комісія за післяплату або грошовий переказ не входять у вартість товару, якщо інше прямо не зазначено під час оформлення замовлення.",
    "Після оплати на рахунок ФОП завантажте квитанцію про оплату та надішліть її менеджеру в застосунку.",
  ],
  paymentMethods: PAYMENT_METHODS_UA,
  links: [
    { label: "Тарифи Нової пошти", href: "https://novaposhta.ua/shipping-cost/" },
    {
      label: "Післяплата / грошовий переказ",
      href: "https://novaposhta.ua/financial-services/money-transfer/to-sender/",
    },
  ],
};

export const RETURNS_NOTE: InfoSection = {
  kind: "returns",
  title: "Обмін і повернення",
  body: [
    "Харчові продукти належної якості не підлягають обміну або поверненню відповідно до переліку товарів, що не підлягають обміну/поверненню. Якщо товар пошкоджений, має ознаки неналежної якості або не відповідає замовленню — зверніться до менеджера одразу після отримання.",
  ],
  links: [
    {
      label: "Закон України «Про захист прав споживачів», ст. 9",
      href: "https://zakon.rada.gov.ua/laws/show/1023-12#Text",
    },
    {
      label: "Постанова КМУ №1243, перелік товарів, що не підлягають обміну/поверненню, пункт «Харчові продукти»",
      href: "https://zakon.rada.gov.ua/laws/show/1243-2024-%D0%BF#Text",
    },
  ],
};

export const FOOD_SAFETY: InfoSection = {
  kind: "food",
  title: "Безпечність та якість харчових продуктів",
  body: [
    "Ми дотримуємося вимог законодавства України щодо безпечності та якості харчових продуктів.",
    "Товари зберігаються і відправляються з урахуванням умов зберігання, зазначених виробником.",
    "Харчові продукти належної якості не підлягають обміну або поверненню відповідно до переліку товарів, що не підлягають обміну/поверненню. Якщо товар пошкоджений, має ознаки неналежної якості або не відповідає замовленню — зверніться до менеджера одразу після отримання.",
  ],
  links: [
    {
      label: "Закон України «Про захист прав споживачів», ст. 9",
      href: "https://zakon.rada.gov.ua/laws/show/1023-12#Text",
    },
    {
      label: "Постанова КМУ №1243, перелік товарів, що не підлягають обміну/поверненню, пункт «Харчові продукти»",
      href: "https://zakon.rada.gov.ua/laws/show/1243-2024-%D0%BF#Text",
    },
  ],
};

export const CONSENT_SHORT: InfoSection = {
  title: "Перед початком",
  body: [
    "Будь ласка, ознайомтеся з умовами оформлення замовлень та обробки персональних даних.",
  ],
};

export const BONUS_REFERRAL_SECTION: InfoSection = {
  kind: "bonuses",
  title: "Бонуси та реферальна програма",
  body: [
    "Це персональна маркетингова знижка, а не гроші, не переказ і не виплата.",
    "Друг отримує 3% на перше оплачене й успішно виконане замовлення. Ви отримуєте 3% на наступну покупку після виконання його замовлення.",
    "Мінімальна сума товарів: 700 ₴. Максимальна вигода: 150 ₴. Термін дії: 30 днів.",
    "Доставка та послуги перевізника не враховуються. Знижки за реферальною програмою не сумуються з іншими акціями, бонусами та промокодами, крім випадків, коли можливість сумування прямо зазначена в умовах відповідної пропозиції. Якщо доступно декілька несумісних знижок, система автоматично застосує найвигіднішу для покупця. Максимальна сумарна знижка: 10%.",
  ],
};

/** Consent + About legal surfaces — contacts accordion intentionally omitted. */
export const INFO_SECTIONS: InfoSection[] = [
  ABOUT_STORE,
  USER_AGREEMENT,
  PRIVACY_POLICY,
  PAYMENT_DELIVERY,
  BONUS_REFERRAL_SECTION,
  RETURNS_NOTE,
  FOOD_SAFETY,
];

